import React, { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@material-ui/core";
import { APIError } from "../../components/APIError";
import { useFallbackUrl, useNavigator } from "../../components/AppHistory";

import {
  CloseButton,
  CustomDialog,
  Field,
} from "../../components/CustomDialog";
import { BillIcon } from "../../icons";
import { ModalComponent } from "../../pages/routes";
import { formatBillPeriod } from "./utils/formatBillPeriod";
import { formatDate } from "../../utils/formatDate";
import { formatMoneyWithCR } from "../../utils/formatMoney";
import { useAccountCredit } from "./hooks/useAccountCredit";
import { BreakdownTable } from "./components/BillBreakdown";
import { DialogLoader } from "../../components/Loader";

export const CreditDetails: ModalComponent = ({ fallbackUrl }) => {
  useFallbackUrl(fallbackUrl);
  const { closeOverlay } = useNavigator();
  const { accountNumber, creditNumber } = useParams();

  const { data: credit, error, isLoading } = useAccountCredit(
    accountNumber,
    creditNumber,
  );

  const onCancel = useCallback(() => {
    closeOverlay();
  }, [closeOverlay]);

  const { billNumber, fromDttm, toDttm, currency, grossAmount } = credit || {};
  const { billDescription, totalAmount } = useMemo(() => {
    return {
      billDescription:
        billNumber == null
          ? undefined
          : `${billNumber} (Period ${formatBillPeriod(fromDttm, toDttm)})`,
      totalAmount:
        grossAmount == null
          ? undefined
          : formatMoneyWithCR(currency, grossAmount, {
              transform: (n) => -n,
            }),
    };
  }, [billNumber, currency, fromDttm, grossAmount, toDttm]);

  return (
    <CustomDialog onClose={onCancel}>
      <DialogTitle data-testid="CreditDetails__Title">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="no-wrap"
        >
          <Box>
            <Box display="flex" alignItems="center">
              <Box mr={0.5} lineHeight={1}>
                <BillIcon height={24} width={24} />
              </Box>
              <Typography variant="h6">Credit Note</Typography>
            </Box>
            <Typography variant="body1">{creditNumber}</Typography>
          </Box>
          <Box mr={-1.5}>
            <CloseButton data-testid="CreditDetails__Button--close" />
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers data-testid="CreditDetails__Content">
        {error ? (
          <APIError
            title="We can't load details for this credit note at the moment."
            mb={45.5}
          />
        ) : isLoading ? (
          <DialogLoader mb={37} />
        ) : (
          credit && (
            <>
              <Grid container justify="space-between">
                <Field
                  label="Issued"
                  value={formatDate(credit.issueDt)}
                  data-testid="Detail__Issued--value"
                />
                <Field
                  label={
                    credit?.transactionStatus === "Rejected"
                      ? "Rejected"
                      : "Authorised"
                  }
                  value={formatDate(credit.resolvedDttm)}
                  data-testid="Detail__Resolved--value"
                />
                <Field
                  label="Status"
                  value={credit.status}
                  data-testid="Detail__Status--value"
                />
                <Field
                  label="Bill credited"
                  value={billDescription}
                  data-testid="Detail__Bill--value"
                />
                <Field
                  label="Reason"
                  value={credit.reason}
                  data-testid="Detail__Reason--value"
                />
                <Field
                  label="Amount (incl. sales tax)"
                  value={totalAmount}
                  data-testid="Detail__Amount--value"
                />
              </Grid>
              {credit.breakdownGroups.length > 0 && (
                <Box my={3}>
                  <BreakdownTable
                    caption="CREDIT NOTE BREAKDOWN"
                    totalAmount={totalAmount!}
                    breakdownGroups={credit.breakdownGroups}
                  />
                </Box>
              )}
            </>
          )
        )}
      </DialogContent>
    </CustomDialog>
  );
};
